import type Graph from 'graphology';
import { parseTimeBucketKey } from './timelineUtils';

// Timeline lookups used to re-read and re-parse every edge's time_buckets on
// every hover, day click and playback tick — O(E × buckets) string → Date work
// per step, 80×/s while playing. This index parses each bucket key once when the
// graph is built and keeps an inverted index bucket → edges, so a step costs only
// the edges that are active in it. It is keyed by graphology edge keys so the
// render-time reducers can test membership with one Set lookup instead of
// rebuilding `${source}-${target}` strings for every edge on every refresh.

type TimeBucketed = { time_buckets?: Record<string, number> | null };

export interface TimelineSelection {
  // graphology edge keys with activity in the selected period
  edges: Set<string>;
  // node ids touched by those edges
  nodes: Set<string>;
}

export interface TimelineIndex {
  // sorted unique bucket timestamps (ms)
  bucketTimes: Float64Array;
  // CSR layout: edges of bucket b are bucketEdges[bucketOffsets[b] … bucketOffsets[b + 1])
  bucketOffsets: Uint32Array;
  bucketEdges: Uint32Array;
  // per DTO edge: its graphology edge key (null when the graph has no such edge)
  // and its endpoints in graph orientation
  edgeKeys: (string | null)[];
  edgeSources: string[];
  edgeTargets: string[];
  // scratch buffer for de-duplicating edges across buckets without allocating
  // per query: an edge is "seen" when its stamp equals the current generation
  stamps: Uint32Array;
  generation: number;
}

export function buildTimelineIndex<T extends TimeBucketed>(
  edges: T[],
  graph: Graph,
  // endpoints in GRAPH orientation (buildGraph flips case edges to
  // amplifier → producer; mention edges keep source → target)
  endpoints: (edge: T) => [string, string],
): TimelineIndex {
  const parsed = new Map<string, number>();
  const timeOf = (key: string): number => {
    let ts = parsed.get(key);
    if (ts === undefined) {
      ts = parseTimeBucketKey(key).getTime();
      parsed.set(key, ts);
    }
    return ts;
  };

  const edgeKeys: (string | null)[] = new Array(edges.length);
  const edgeSources: string[] = new Array(edges.length);
  const edgeTargets: string[] = new Array(edges.length);
  // per edge the timestamps of its buckets (flat) — unique times collected on the way
  const edgeTimes: number[][] = new Array(edges.length);
  const uniqueTimes = new Set<number>();
  let entries = 0;
  edges.forEach((edge, i) => {
    const [source, target] = endpoints(edge);
    edgeSources[i] = source;
    edgeTargets[i] = target;
    edgeKeys[i] = graph.hasNode(source) && graph.hasNode(target) ? graph.edge(source, target) ?? null : null;
    const times: number[] = [];
    if (edge.time_buckets) {
      for (const key of Object.keys(edge.time_buckets)) {
        const ts = timeOf(key);
        if (Number.isNaN(ts)) continue;
        times.push(ts);
        uniqueTimes.add(ts);
      }
    }
    edgeTimes[i] = times;
    entries += times.length;
  });

  const bucketTimes = Float64Array.from(uniqueTimes).sort();
  const bucketOf = new Map<number, number>();
  bucketTimes.forEach((ts, b) => bucketOf.set(ts, b));

  const bucketOffsets = new Uint32Array(bucketTimes.length + 1);
  edgeTimes.forEach((times) => {
    times.forEach((ts) => {
      bucketOffsets[(bucketOf.get(ts) as number) + 1] += 1;
    });
  });
  for (let b = 0; b < bucketTimes.length; b++) bucketOffsets[b + 1] += bucketOffsets[b];

  const bucketEdges = new Uint32Array(entries);
  const fill = bucketOffsets.slice(0, bucketTimes.length);
  edgeTimes.forEach((times, i) => {
    times.forEach((ts) => {
      const b = bucketOf.get(ts) as number;
      bucketEdges[fill[b]] = i;
      fill[b] += 1;
    });
  });

  return {
    bucketTimes,
    bucketOffsets,
    bucketEdges,
    edgeKeys,
    edgeSources,
    edgeTargets,
    stamps: new Uint32Array(edges.length),
    generation: 0,
  };
}

// first bucket with time >= ts
function lowerBound(times: Float64Array, ts: number): number {
  let lo = 0;
  let hi = times.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (times[mid] < ts) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

// first bucket with time > ts
function upperBound(times: Float64Array, ts: number): number {
  let lo = 0;
  let hi = times.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (times[mid] <= ts) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

function collect(index: TimelineIndex, fromBucket: number, toBucket: number): TimelineSelection {
  index.generation += 1;
  const { stamps, generation, bucketOffsets, bucketEdges, edgeKeys, edgeSources, edgeTargets } = index;
  const edges = new Set<string>();
  const nodes = new Set<string>();
  for (let b = fromBucket; b < toBucket; b++) {
    for (let k = bucketOffsets[b]; k < bucketOffsets[b + 1]; k++) {
      const e = bucketEdges[k];
      if (stamps[e] === generation) continue;
      stamps[e] = generation;
      const key = edgeKeys[e];
      if (key === null) continue;
      edges.add(key);
      nodes.add(edgeSources[e]);
      nodes.add(edgeTargets[e]);
    }
  }
  return { edges, nodes };
}

// edges with at least one bucket inside [start, end] (inclusive, like the
// per-edge scan it replaces)
export function selectTimeRange(index: TimelineIndex, [start, end]: [Date, Date]): TimelineSelection {
  const from = lowerBound(index.bucketTimes, start.getTime());
  const to = upperBound(index.bucketTimes, end.getTime());
  return collect(index, from, Math.max(from, to));
}

// edges with a bucket exactly at `date` (a bar of the timeline)
export function selectTimePeriod(index: TimelineIndex, date: Date): TimelineSelection {
  const ts = date.getTime();
  const b = lowerBound(index.bucketTimes, ts);
  return b < index.bucketTimes.length && index.bucketTimes[b] === ts
    ? collect(index, b, b + 1)
    : collect(index, 0, 0);
}
